import { COMPANY_FILTER_CHANGE } from 'containers/PartnerOptions/constants'

import {
  CHANGE_MESSAGE_ACCESS_ASYNC,
  CLEAR_TRACKED_EMAIL_DETAIL,
  FETCH_TRACKED_EMAIL_DETAIL_ASYNC,
  FETCH_TRACKED_EMAILS_ASYNC,
  FETCH_TRACKED_EMAILS_SUMMARY_ASYNC,
  FETCH_TRIGGER_POLICIES_CONFIG_ASYNC,
  FETCH_TRIGGER_POLICIES_SCHEMA_ASYNC,
  GO_TO_TRACKED_EMAILS_NEXT_PAGE,
  GO_TO_TRACKED_EMAILS_PREV_PAGE,
  SET_TRACKED_EMAILS_FILTERS,
} from './constants'
import { isOtherCompany } from './mapTrackedEmails'

const emptySummary = {
  summary: null,
  summaryLoaded: false,
  summaryError: null,
  windowFrom: null,
  windowTo: null,
  plan: null,
}

const initialState = {
  items: [],
  nextCursor: null,
  total: 0,
  ...emptySummary,
  summaryCompanyId: null,
  loading: false,
  loaded: false,
  error: null,
  filters: {},
  cursor: null,
  cursorHistory: [],
  selectedDetail: null,
  detailLoading: false,
  detailError: null,
  schema: [],
  config: null,
  schemaLoader: false,
  configLoader: false,
  schemaLoaded: false,
  configLoaded: false,
  schemaError: null,
  configError: null,
  accessError: null,
  accessNotice: null,
  accessPending: 0,
  accessSucceeded: 0,
  accessFailed: 0,
  accessFailureMessages: [],
}

const resetPagination = {
  cursor: null,
  cursorHistory: [],
}

export default function emailTrackingReducer(state = initialState, action = {}) {
  switch (action.type) {
    case SET_TRACKED_EMAILS_FILTERS:
      return {
        ...state,
        filters: action.payload?.filters || {},
        ...resetPagination,
      }

    case GO_TO_TRACKED_EMAILS_NEXT_PAGE:
      if (!state.nextCursor) return state
      return {
        ...state,
        cursorHistory: [...state.cursorHistory, state.cursor],
        cursor: state.nextCursor,
      }

    case GO_TO_TRACKED_EMAILS_PREV_PAGE: {
      if (state.cursorHistory.length === 0) return state
      const cursorHistory = state.cursorHistory.slice(0, -1)
      const cursor = state.cursorHistory.at(-1)
      return {
        ...state,
        cursorHistory,
        cursor,
      }
    }

    case FETCH_TRACKED_EMAILS_ASYNC.INITIATE:
      return { ...state, loading: true, error: null }
    case FETCH_TRACKED_EMAILS_ASYNC.SUCCESS:
      return {
        ...state,
        loading: false,
        loaded: true,
        items: action.payload?.data?.items || [],
        nextCursor: action.payload?.data?.nextCursor ?? null,
        total: action.payload?.data?.total ?? 0,
      }
    case FETCH_TRACKED_EMAILS_ASYNC.ERROR:
      return { ...state, loading: false, loaded: true, error: action.error }

    case FETCH_TRACKED_EMAILS_SUMMARY_ASYNC.INITIATE:
      return {
        ...state,
        summaryLoaded: false,
        summaryError: null,
        summaryCompanyId: action.payload?.companyId ?? null,
      }
    case FETCH_TRACKED_EMAILS_SUMMARY_ASYNC.SUCCESS:
      return {
        ...state,
        summary: action.payload?.data?.summary ?? null,
        windowFrom: action.payload?.data?.windowFrom ?? null,
        windowTo: action.payload?.data?.windowTo ?? null,
        plan: action.payload?.data?.plan ?? null,
        summaryLoaded: true,
        summaryError: null,
      }
    case FETCH_TRACKED_EMAILS_SUMMARY_ASYNC.ERROR:
      return { ...state, ...emptySummary, summaryLoaded: true, summaryError: action.error }

    case COMPANY_FILTER_CHANGE:
      if (!isOtherCompany(action, state.summaryCompanyId)) return state
      return {
        ...state,
        ...emptySummary,
        ...resetPagination,
        items: [],
        nextCursor: null,
        total: 0,
        loaded: false,
      }

    case FETCH_TRACKED_EMAIL_DETAIL_ASYNC.INITIATE:
      return { ...state, detailLoading: true, detailError: null }
    case FETCH_TRACKED_EMAIL_DETAIL_ASYNC.SUCCESS: {
      const selectedDetail = action.payload?.data ?? null
      return {
        ...state,
        detailLoading: false,
        selectedDetail,
        items: state.items.map((item) =>
          selectedDetail && item.id === selectedDetail.id ? selectedDetail : item
        ),
      }
    }
    case CHANGE_MESSAGE_ACCESS_ASYNC.INITIATE: {
      const starting = state.accessPending === 0
      return {
        ...state,
        accessError: null,
        accessPending: state.accessPending + 1,
        accessSucceeded: starting ? 0 : state.accessSucceeded,
        accessFailed: starting ? 0 : state.accessFailed,
        accessFailureMessages: starting ? [] : state.accessFailureMessages,
      }
    }
    case CHANGE_MESSAGE_ACCESS_ASYNC.SUCCESS: {
      const access = action.payload?.access
      return {
        ...state,
        accessPending: Math.max(0, state.accessPending - 1),
        accessSucceeded: state.accessSucceeded + 1,
        accessNotice: {
          type: 'success',
          text: access === 'restore' ? 'Email access restored' : 'Email access revoked',
        },
      }
    }
    case CHANGE_MESSAGE_ACCESS_ASYNC.ERROR: {
      const detail = action.error?.detail || 'Could not update email access.'
      return {
        ...state,
        accessPending: Math.max(0, state.accessPending - 1),
        accessFailed: state.accessFailed + 1,
        accessFailureMessages: state.accessFailureMessages.includes(detail)
          ? state.accessFailureMessages
          : [...state.accessFailureMessages, detail],
        accessError: detail,
      }
    }
    case CHANGE_MESSAGE_ACCESS_ASYNC.CANCEL:
      return { ...state, accessError: null, accessNotice: null }
    case FETCH_TRACKED_EMAIL_DETAIL_ASYNC.ERROR:
      return { ...state, detailLoading: false, detailError: action.error }
    case CLEAR_TRACKED_EMAIL_DETAIL:
      return { ...state, selectedDetail: null, detailLoading: false, detailError: null }

    case FETCH_TRIGGER_POLICIES_SCHEMA_ASYNC.INITIATE:
      return { ...state, schemaLoader: true, schemaError: null }
    case FETCH_TRIGGER_POLICIES_SCHEMA_ASYNC.SUCCESS:
      return {
        ...state,
        schemaLoader: false,
        schemaLoaded: true,
        schemaError: null,
        schema: Array.isArray(action.payload?.data) ? action.payload.data : [],
      }
    case FETCH_TRIGGER_POLICIES_SCHEMA_ASYNC.ERROR:
      return {
        ...state,
        schemaLoader: false,
        schemaLoaded: true,
        schemaError: action.error,
      }

    case FETCH_TRIGGER_POLICIES_CONFIG_ASYNC.INITIATE:
      return { ...state, configLoader: true, configError: null }
    case FETCH_TRIGGER_POLICIES_CONFIG_ASYNC.SUCCESS:
      return {
        ...state,
        configLoader: false,
        configLoaded: true,
        configError: null,
        config: action.payload?.data ?? null,
      }
    case FETCH_TRIGGER_POLICIES_CONFIG_ASYNC.ERROR:
      return {
        ...state,
        configLoader: false,
        configLoaded: true,
        configError: action.error,
      }

    default:
      return state
  }
}
